import React, { useEffect, useState } from 'react'
import GoogleLoginButton from './GoogleLoginButton'
import RedditLoginButton from './RedditLoginButton'
import { apiClient } from '../../lib/api'

interface OAuthButtonsProps {
  mode: 'login' | 'register'
  onSuccess?: () => void
}

interface OAuthConfig {
  available_providers: {
    google?: {
      provider: string
      configured: boolean
      name: string
    }
    reddit?: {
      provider: string
      configured: boolean
      name: string
    }
  }
  endpoints: {
    authorize: string
    callback: string
    link: string
    unlink: string
    accounts: string
  }
}

const OAuthButtons: React.FC<OAuthButtonsProps> = ({ mode, onSuccess }) => {
  const [oauthConfig, setOauthConfig] = useState<OAuthConfig | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadOAuthConfig = async () => {
      try {
        const response = await apiClient.request<{
          success: boolean
          data: OAuthConfig
        }>('/wp-json/redd-miner-leads/v1/oauth/config')

        if (response.success) {
          setOauthConfig(response.data)
        }
      } catch (error) {
        console.error('Failed to load OAuth config:', error)
      } finally {
        setLoading(false)
      }
    }

    loadOAuthConfig()
  }, [])

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="animate-pulse">
          <div className="h-11 bg-gray-200 rounded-lg"></div>
        </div>
        <div className="animate-pulse">
          <div className="h-11 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    )
  }

  if (!oauthConfig || !oauthConfig.available_providers) {
    return null
  }

  const hasProviders = Object.keys(oauthConfig.available_providers).length > 0
  const googleConfigured = oauthConfig.available_providers.google?.configured
  const redditConfigured = oauthConfig.available_providers.reddit?.configured

  if (!hasProviders || (!googleConfigured && !redditConfigured)) {
    return null
  }

  return (
    <div className="space-y-3">
      {googleConfigured && (
        <GoogleLoginButton
          mode={mode}
          onSuccess={onSuccess}
        />
      )}

      {redditConfigured && (
        <RedditLoginButton
          mode={mode}
          onSuccess={onSuccess}
        />
      )}

      {(googleConfigured || redditConfigured) && (
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="bg-white px-2 text-gray-500">
              {mode === 'login' ? 'Or sign in with email' : 'Or create account with email'}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default OAuthButtons